import React, { useState, useEffect, useMemo } from 'react';
import { X, Zap } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import { getFavorites } from '../pages/Markets';

interface Props {
  dark?: boolean;
  defaultSymbol?: string;
  defaultLeverage?: number;
  defaultQuantity?: number;
  defaultSide?: 'long' | 'short';
  defaultTaker?: boolean;
  onClose: () => void;
}

export default function ManualOrderModal({ dark = true, defaultSymbol, defaultLeverage, defaultQuantity, defaultSide, defaultTaker = false, onClose }: Props) {
  const { tickers, sendWs, subscribe, account, marketInfo } = useWS();
  const t = getTheme(dark);

  // Aktif botun ticker sembollerini al — bot'a özgüdür
  const botSymbols = useMemo(() => (tickers as any[]).map(tk => tk.symbol).filter(Boolean), [tickers]);

  // Favorileri aktif bota göre süz; eşleşen yoksa botun tüm sembollerini kullan
  const symbolList = useMemo(() => {
    if (botSymbols.length === 0) {
      const favs = getFavorites();
      return favs.length > 0 ? favs : ['PERP_SOL_USDC'];
    }
    const favs    = getFavorites();
    const botFavs = favs.filter(f => botSymbols.includes(f));
    return botFavs.length > 0 ? botFavs : botSymbols;
  }, [botSymbols]);

  const [side,     setSide]     = useState<'long' | 'short'>(defaultSide || 'long');
  const [symbol,   setSymbol]   = useState(defaultSymbol || symbolList[0]);
  const [price,    setPrice]    = useState('');
  const [quantity, setQuantity] = useState(defaultQuantity != null ? String(defaultQuantity) : '');

  // Taker modunda açılışta fiyatı otomatik doldur (ticker yüklenince)
  const takerFillRef = React.useRef(false);
  useEffect(() => {
    if (defaultTaker && !takerFillRef.current) {
      const tk = (tickers as any[]).find((t: any) => t.symbol === symbol);
      if (tk) {
        const fillPrice = (defaultSide === 'long' || !defaultSide) ? tk.best_ask : tk.best_bid;
        if (fillPrice) { setPrice(fillPrice.toFixed(6)); takerFillRef.current = true; }
      }
    }
  }, [tickers, defaultTaker, defaultSide, symbol]);
  const [leverage, setLeverage] = useState(defaultLeverage ?? 20);
  const [isTaker,  setIsTaker]  = useState(defaultTaker);  // false = maker (postOnly), true = taker
  const [result,   setResult]   = useState<{ success: boolean; msg: string } | null>(null);
  const [loading,  setLoading]  = useState(false);

  const ticker = useMemo(() => tickers.find(tk => tk.symbol === symbol), [tickers, symbol]);

  // ── Paritenin izin verilen azami kaldıracı ────────────────────────────────
  // Veri zaten `market_info` mesajıyla geliyor; modal okumuyordu.
  // Borsalara göre durum farklı, etiketi ona göre kuruyoruz:
  //   HL / KuCoin / Extended → borsanın ilan ettiği kesin değer
  //   Binance / Aster        → tavan pozisyon BÜYÜKLÜĞÜNE bağlı; ikinci kademe
  //                            eşiğini de yazıyoruz ("maks 50 · $5.000 üstünde 20")
  //   Bluefin                → borsa yayınlamıyor, IMR'den türetiliyor (yaklaşık)
  //   Orderly                → parite bazlı limit YOK, hiçbir şey gösterme
  const levInfo = useMemo(() => {
    const mi = (marketInfo || {})[symbol];
    if (!mi) return null;
    const max = Number(mi.max_leverage || 0);
    if (!(max > 0)) return null;
    const cap  = Number(mi.lev_cap_notional || 0);
    const next = Number(mi.lev_next || 0);
    // Borsa emirdeki kaldıracı uygulamıyorsa (KuCoin CROSS) bunu SÖYLEMEK
    // zorundayız: kullanıcı 20 yazıp 75x'ten işleme giriyordu.
    const eff  = Number(mi.effective_leverage || 0);
    return { max, cap, next, eff };
  }, [marketInfo, symbol]);

  // ── Miktar adıma uyuyor mu ────────────────────────────────────────────────
  // Adıma uymayan emri Bluefin KABUL EDİP hash döndürüyor, sonra sessizce
  // öldürüyor: tahtada hiç görünmüyor, hata da dönmüyor. 07/09'da HYPE-PERP'te
  // adım 0,1 iken 0,69 gönderildi ve strateji 356 kez boşa denedi. Köprü artık
  // aşağı yuvarlıyor; burada da ne göndereceğimizi ÖNCEDEN söylüyoruz.
  const stepWarn = useMemo(() => {
    const mi = (marketInfo || {})[symbol];
    const step = Number(mi?.step_size || 0);
    const q = parseFloat(quantity);
    if (!(step > 0) || !(q > 0)) return null;
    const snapped = Math.floor(q / step + 1e-9) * step;
    const dec = Math.max(0, (String(step).split('.')[1] || '').length);
    const shown = Number(snapped.toFixed(dec));
    if (Math.abs(shown - q) < 1e-12) return null;
    const min = Number(mi?.min_qty || 0);
    if (!(shown > 0) || (min > 0 && shown < min)) {
      return { blocking: true, msg: `Miktar adıma (${step}) oturmuyor ve en az ${min || step} olmalı — emir gönderilemez` };
    }
    return { blocking: false, msg: `Adım ${step}: borsaya ${shown} olarak gidecek` };
  }, [marketInfo, symbol, quantity]);

  const levText = useMemo(() => {
    if (!levInfo) return '';
    const { max, cap, next, eff } = levInfo;
    if (eff > 0) return `borsa ${eff}x uyguluyor · maks ${max}`;
    if (cap > 0 && next > 0) {
      const capTxt = cap >= 1000
        ? `$${(cap / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}b`
        : `$${cap.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
      return `maks ${max} · ${capTxt} üstünde ${next}`;
    }
    return `maks ${max}`;
  }, [levInfo]);

  const mid       = ticker ? ((ticker.best_bid + ticker.best_ask) / 2) : null;
  const bbo       = ticker ? (side === 'long' ? ticker.best_bid  : ticker.best_ask) : null;
  // Taker için crossing price: long → ask (fiyat >= ask = taker), short → bid
  const takerFill = ticker ? (side === 'long' ? ticker.best_ask  : ticker.best_bid)  : null;

  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'manual_order_result') {
        setLoading(false);
        const p = data.payload;
        setResult({ success: p.success, msg: p.success ? 'Emir gönderildi ✓' : (p.error || 'Hata') });
        if (p.success) {
          setPrice('');
          setQuantity('');
          setTimeout(() => setResult(null), 3000);
        }
      }
    });
  }, [subscribe]);

  // Sembol değişince fiyatı sıfırla
  useEffect(() => { setPrice(''); }, [symbol]);

  const notional = (parseFloat(price) || 0) * (parseFloat(quantity) || 0);

  const send = () => {
    const p = parseFloat(price);
    const q = parseFloat(quantity);
    if (!p || p <= 0 || !q || q <= 0) return;
    setLoading(true);
    setResult(null);
    sendWs({
      type: 'manual_order',
      payload: { symbol, side, price: p, quantity: q, leverage, postOnly: !isTaker },
    });
  };

  const inp = `border rounded-lg px-3 py-2 text-sm focus:outline-none w-full ${t.inp}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}>
      <div
        className={`${t.card} border ${t.cardBorder} rounded-2xl w-[360px] shadow-2xl`}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${t.cardBorder}`}>
          <select
            className={`text-sm font-bold bg-transparent focus:outline-none ${t.text} cursor-pointer`}
            value={symbol}
            onChange={e => setSymbol(e.target.value)}>
            {symbolList.map(s => (
              <option key={s} value={s}>{s.replace('PERP_','').replace('_USDC','').replace('-PERP','')}</option>
            ))}
          </select>
          <button onClick={onClose} className={`${t.muted} hover:text-rose-400 transition-colors`}>
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3">

          {/* Buy / Sell tabs */}
          <div className="flex rounded-lg overflow-hidden border border-white/10">
            <button
              onClick={() => setSide('long')}
              className={`flex-1 py-2 text-sm font-bold transition-colors ${
                side === 'long'
                  ? 'bg-emerald-600 text-white'
                  : `${dark ? 'bg-white/5 text-gray-400 hover:bg-emerald-900/30' : 'bg-gray-100 text-gray-400 hover:bg-emerald-50'}`
              }`}>
              Buy / Long
            </button>
            <button
              onClick={() => setSide('short')}
              className={`flex-1 py-2 text-sm font-bold transition-colors ${
                side === 'short'
                  ? 'bg-rose-600 text-white'
                  : `${dark ? 'bg-white/5 text-gray-400 hover:bg-rose-900/30' : 'bg-gray-100 text-gray-400 hover:bg-rose-50'}`
              }`}>
              Sell / Short
            </button>
          </div>

          {/* Kaldıraç + Maker/Taker toggle */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className={`text-xs block mb-1 ${t.muted}`}>
                Kaldıraç
                {levText && (
                  <span className="ml-1.5 opacity-70"
                    title={levInfo && levInfo.eff > 0
                      ? `Bu borsa CROSS kipte emirdeki kaldıracı YOK SAYIYOR; hesap ayarı ${levInfo.eff}x uygulanıyor. Değiştirmek için borsa arayüzünden hesap kaldıracını ayarlayın.`
                      : levInfo && levInfo.cap > 0
                      ? 'Borsa tavanı pozisyon büyüklüğüne göre düşüyor — nominal eşiği aşınca daha yükseği reddedilir'
                      : 'Borsanın bu parite için ilan ettiği azami kaldıraç'}>
                    ({levText})
                  </span>
                )}
              </label>
              {/* TAVANI AŞARSA KIRMIZI. Aşan emir sessizce ölüyor: Bluefin
                  DEEP-PERP'te 22x gönderdik, borsa hash döndürdü ama emir hiç
                  defterde görünmedi. Girişi kilitlemiyoruz — bazı borsalarda
                  eşik miktara bağlı — ama uyarmadan geçmiyoruz. */}
              <input type="number" min="1" max="200" className={`${inp} ${
                levInfo && leverage > levInfo.max ? 'border-rose-500 text-rose-300' : ''}`}
                value={leverage}
                onChange={e => setLeverage(+e.target.value)} />
              {levInfo && leverage > levInfo.max && (
                <p className="text-[10px] text-rose-400 mt-1">
                  Borsa tavanı {levInfo.max}x — üstü reddedilir
                </p>
              )}
            </div>
            {/* Maker | Taker pill toggle */}
            <div className="flex flex-col items-center gap-1 pt-4 shrink-0">
              <div className="flex rounded-lg overflow-hidden border border-white/15 text-[11px] font-bold">
                <button
                  onClick={() => setIsTaker(false)}
                  className={`px-2.5 py-1.5 transition-colors ${
                    !isTaker
                      ? 'bg-[#4A9FE8] text-[#061530]'
                      : `${dark ? 'bg-white/5 text-gray-500 hover:bg-white/10' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`
                  }`}>
                  Maker
                </button>
                <button
                  onClick={() => {
                    setIsTaker(true);
                    if (takerFill) setPrice(takerFill.toFixed(6));
                  }}
                  className={`px-2.5 py-1.5 transition-colors flex items-center gap-1 ${
                    isTaker
                      ? 'bg-orange-500 text-white'
                      : `${dark ? 'bg-white/5 text-gray-500 hover:bg-orange-900/30 hover:text-orange-400' : 'bg-gray-100 text-gray-400 hover:bg-orange-50'}`
                  }`}>
                  <Zap size={10} />Taker
                </button>
              </div>
            </div>
          </div>

          {/* Taker uyarı bandı */}
          {isTaker && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-orange-900/30 border border-orange-500/40 text-[11px] text-orange-300">
              <Zap size={11} className="shrink-0" />
              Taker modu — emir anında piyasaya çarpar, komisyon daha yüksek.
            </div>
          )}

          {/* Fiyat */}
          <div>
            <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat (USDC)</label>
            <div className="flex gap-1.5">
              <input type="number" step="0.0001" min="0" placeholder="0.0000"
                className={`${inp} ${isTaker ? 'border-orange-500/60 focus:border-orange-400' : ''}`}
                value={price}
                onChange={e => setPrice(e.target.value)} />
              {isTaker ? (
                /* Taker modunda: crossing-price butonu */
                <button
                  onClick={() => takerFill && setPrice(takerFill.toFixed(6))}
                  disabled={!takerFill}
                  className="px-2.5 py-2 rounded-lg text-xs font-bold border border-orange-500/60 text-orange-300 hover:bg-orange-900/30 disabled:opacity-40 whitespace-nowrap flex items-center gap-1">
                  <Zap size={10} />{side === 'long' ? 'Ask' : 'Bid'}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => bbo && setPrice(bbo.toFixed(6))}
                    disabled={!bbo}
                    className="px-2.5 py-2 rounded-lg text-xs font-bold border border-white/20 text-gray-300 hover:bg-white/10 disabled:opacity-40 whitespace-nowrap">
                    BBO
                  </button>
                  <button
                    onClick={() => mid && setPrice(mid.toFixed(6))}
                    disabled={!mid}
                    className="px-2.5 py-2 rounded-lg text-xs font-bold border border-white/20 text-gray-300 hover:bg-white/10 disabled:opacity-40 whitespace-nowrap">
                    Mid
                  </button>
                </>
              )}
            </div>
            {ticker && (
              <p className={`text-[10px] mt-1 ${t.muted}`}>
                Bid: <span className="text-emerald-400">{ticker.best_bid.toFixed(6)}</span>
                {' · '}
                Ask: <span className="text-rose-400">{ticker.best_ask.toFixed(6)}</span>
                {' · '}
                Mark: <span className={t.text}>{ticker.mark_price.toFixed(6)}</span>
              </p>
            )}
          </div>

          {/* Miktar */}
          <div>
            <label className={`text-xs block mb-1 ${t.muted}`}>
              Miktar&nbsp;
              <span className={t.muted}>({symbol.replace('PERP_','').replace('_USDC','').replace('-PERP','')})</span>
            </label>
            <input type="number" step="0.001" min="0" placeholder="0.000"
              className={inp}
              value={quantity}
              onChange={e => setQuantity(e.target.value)} />
            {stepWarn && (
              <p className={`text-[10px] mt-1 ${stepWarn.blocking ? 'text-rose-400' : 'text-amber-400/90'}`}>
                {stepWarn.msg}
              </p>
            )}
            {notional > 0 && (
              <p className={`text-[10px] mt-1 ${t.muted}`}>
                Notional: <span className={t.text}>${notional.toFixed(2)} USDC</span>
              </p>
            )}
          </div>

          {/* Available */}
          {account && (
            <p className={`text-xs ${t.muted}`}>
              Kullanılabilir: <span className={`font-mono ${t.text}`}>${account.availableBalance.toFixed(2)} USDC</span>
            </p>
          )}

          {/* Sonuç */}
          {result && (
            <div className={`text-xs text-center py-1.5 rounded-lg font-medium ${
              result.success ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
            }`}>
              {result.msg}
            </div>
          )}

          {/* Gönder butonu */}
          <button
            onClick={send}
            disabled={!price || !quantity || loading}
            className={`w-full py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5 ${
              isTaker
                ? 'bg-orange-600 hover:bg-orange-500 text-white'
                : side === 'long'
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'bg-rose-600 hover:bg-rose-500 text-white'
            }`}>
            {loading
              ? 'Gönderiliyor...'
              : isTaker
                ? <><Zap size={14} />{side === 'long' ? 'Taker Buy' : 'Taker Sell'}</>
                : side === 'long' ? 'Buy / Long' : 'Sell / Short'
            }
          </button>

        </div>
      </div>
    </div>
  );
}
